/**
 * Settings: "Spoken updates" — hear "N five five five V X has taken off" / "Heron, northeast, two
 * miles, inbound" and the producer's calls ("Heron, taxiing.") with an AirPod in instead of looking
 * at the phone. Preferences live on the server (the Airview Studio app follows them with the phone locked);
 * the server decides what to say. The voice is picked per device (lib/speech).
 */
import { useEffect, useState } from 'react';
import type { CalloutPeriodicSec, Id } from '@apos/shared';
import { getVoicePref, listVoices, nativeSpeaksCallouts, onVoicesChanged, setVoicePref, speakNow, speechSupported, type VoiceInfo } from '../lib/speech';
import { useCallouts } from '../state/callouts';
import { useMission } from '../state/mission';
import { RecentCallouts } from '../features/callouts/RecentCallouts';
import { Button, cx, Segmented, Select, Toggle } from './ui';
import '../features/callouts/callouts.css';

const ROLE_LABEL: Record<string, string> = { subject: 'Subject', photo_ship: 'Chase', chase: 'Chase', other: 'Watch' };
const isDefaultRole = (role: string) => role === 'subject';

export function SpokenUpdatesSetting() {
  const bundle = useMission((s) => s.bundle);
  const prefs = useCallouts((s) => s.prefs);
  const error = useCallouts((s) => s.error);
  const update = useCallouts((s) => s.update);
  const enabled = prefs?.enabled ?? false;
  const native = nativeSpeaksCallouts();
  const aircraft = bundle?.aircraft ?? [];
  const chosen = new Set<Id>(prefs?.aircraftIds ?? aircraft.filter((a) => isDefaultRole(a.role)).map((a) => a.id));

  const toggleAircraft = (id: Id) => {
    const next = new Set(chosen);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    const defaults = aircraft.filter((a) => isDefaultRole(a.role)).map((a) => a.id);
    const isDefault = next.size === defaults.length && defaults.every((d) => next.has(d));
    void update({ aircraftIds: isDefault ? null : aircraft.filter((a) => next.has(a.id)).map((a) => a.id) });
  };

  return (
    <div className="spoken-setting" data-testid="spoken-setting">
      <Toggle
        checked={enabled}
        disabled={!prefs && !error}
        onChange={(on) => {
          // this tap is the user gesture iOS needs before the page may speak
          if (on) speakNow('Spoken updates on.');
          void update({ enabled: on });
        }}
        label="Spoken updates"
      />
      <div className="spoken-setting__hint">
        {native
          ? 'The Airview Studio app speaks them — works with the phone locked and through AirPods.'
          : speechSupported()
            ? 'Producer calls, takeoff, landing, distance, in view / behind terrain. Keep this page open.'
            : 'This browser cannot speak.'}
      </div>
      {error ? <div className="spoken-setting__error">{error}</div> : null}
      {enabled ? (
        <>
          <p className="spoken-setting__hint">Subject updates are on by default. Select Chase or Watch aircraft when you want to hear about them.</p>
          {aircraft.length ? (
            <div className="spoken-setting__aircraft" role="group" aria-label="Aircraft to hear about">
              {aircraft.map((a) => (
                <button key={a.id} type="button" className={cx('spoken-chip', chosen.has(a.id) && 'is-on')} aria-pressed={chosen.has(a.id)} onClick={() => toggleAircraft(a.id)}>
                  {a.label}
                  <small>{ROLE_LABEL[a.role] ?? ''}</small>
                </button>
              ))}
            </div>
          ) : null}
          <div className="menu-pref">
            <span>Distance every</span>
            <Segmented<string>
              size="sm"
              label="Distance and direction every"
              value={String(prefs?.periodicSec ?? 0)}
              onChange={(v) => void update({ periodicSec: Number(v) as CalloutPeriodicSec })}
              options={[
                { value: '0', label: 'Off' },
                { value: '30', label: '30 s' },
                { value: '60', label: '60 s' },
                { value: '120', label: '2 min' },
              ]}
            />
          </div>
          <VoicePicker testText={testLine(aircraft[0]?.label)} />
          {bundle ? <RecentCallouts missionId={bundle.mission.id} limit={3} /> : null}
        </>
      ) : null}
    </div>
  );
}

function testLine(label?: string): string {
  return `${label ? label.split(/\s+/)[0] : 'Aircraft'}, northeast, two miles, inbound.`;
}

/** Voice for spoken updates on this device: the most natural voice by default, or the one picked here. */
export function VoicePicker({ testText }: { testText: string }) {
  const [voices, setVoices] = useState<VoiceInfo[]>(() => listVoices());
  const [pick, setPick] = useState<string>(() => getVoicePref() ?? '');
  useEffect(() => {
    const refresh = () => setVoices(listVoices());
    refresh();
    const off = onVoicesChanged(refresh);
    const t = setTimeout(refresh, 600); // Safari fills the list late and may not fire voiceschanged
    return () => {
      off();
      clearTimeout(t);
    };
  }, []);
  const best = voices[0];
  return (
    <div className="spoken-setting__voice">
      {voices.length > 1 ? (
        <div className="menu-pref">
          <span>Voice</span>
          <Select
            value={voices.some((v) => v.id === pick) ? pick : ''}
            aria-label="Voice"
            onChange={(e) => {
              setPick(e.target.value);
              setVoicePref(e.target.value || null);
              speakNow(testText);
            }}
          >
            <option value="">{best ? `Best: ${best.name}` : 'Best available'}</option>
            {voices.map((v) => (
              <option key={v.id} value={v.id}>
                {voiceLabel(v)}
              </option>
            ))}
          </Select>
        </div>
      ) : null}
      <div className="spoken-setting__row">
        <Button size="sm" icon="radio" onClick={() => speakNow(testText)}>
          Test voice
        </Button>
      </div>
    </div>
  );
}

/** "Ava (Premium) · en-US", "Google US English · online". */
export function voiceLabel(v: Pick<VoiceInfo, 'name' | 'lang' | 'online'>): string {
  return `${v.name.replace(/^Microsoft\s+/, '')} · ${v.online ? 'online' : v.lang}`;
}
